import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, CircularProgress, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Divider, TextField, MenuItem, Checkbox, FormControlLabel, Alert
} from '@mui/material';
import {
  Home, Add, Edit, Delete, Assignment, Send, CheckCircle, Cancel,
  RequestQuote, ArrowForward
} from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { fmt, sym } from '../utils/currency';
import { useItems } from '../hooks/useItems';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { useVATGroups } from '../hooks/useVATGroups';
import { useLocations } from '../hooks/useLocations';
import { useSuppliers } from '../hooks/useSuppliers';
import { glAccountOptions } from '../utils/glAccountOptions';

const API = process.env.REACT_APP_API_URL;
const LINE_TYPES = ['Item', 'Service', 'GL Account'];

const STATUS_COLOR = {
  Draft: 'default', Submitted: 'warning', Approved: 'primary',
  Rejected: 'error', Quoted: 'success', Cancelled: 'default',
};

const emptyLine = () => ({
  _key: Date.now() + Math.random(),
  lineType: 'Item', itemNo: '', glAccount: '', description: '',
  qty: 1, estUnitCost: '', vatGroupCode: '',
});
const emptyHeader = () => ({
  requisitionDate: new Date().toISOString().split('T')[0],
  requestedBy: '', department: '', locationCode: '', neededByDate: '', purpose: '',
});

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);
const d = (v) => (v ? new Date(v).toLocaleDateString() : '—');

const PurchaseRequisitionList = () => {
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const { items: allItems }      = useItems();
  const { accounts: glAccounts } = useGLAccounts();
  const { vatGroups }            = useVATGroups();
  const { locations }            = useLocations();
  const { suppliers }            = useSuppliers();

  const [reqs,    setReqs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [busy,    setBusy]    = useState(false);

  // Create / edit dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editing,  setEditing]  = useState(null);   // requisitionNo when editing
  const [header,   setHeader]   = useState(emptyHeader());
  const [lines,    setLines]    = useState([emptyLine()]);
  const [saving,   setSaving]   = useState(false);

  const [viewPR,   setViewPR]   = useState(null);
  const [cancelPR, setCancelPR] = useState(null);
  const [rejectPR, setRejectPR] = useState(null);
  const [rejReason, setRejReason] = useState('');
  const [genPR,    setGenPR]    = useState(null);
  const [genSel,   setGenSel]   = useState([]);      // selected supplierCodes

  const fetchReqs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/payables/purchase-requisitions`, { headers });
      setReqs(data); setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load requisitions');
    } finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchReqs(); }, [fetchReqs]);

  const itemsOfType = (type) => allItems.filter(i => i.itemType === type);

  const updateLine = (key, field, value) => {
    setLines(prev => prev.map(l => {
      if (l._key !== key) return l;
      const u = { ...l, [field]: value };
      if (field === 'lineType') { u.itemNo = ''; u.glAccount = ''; u.description = ''; u.estUnitCost = ''; }
      if (field === 'itemNo' && value) {
        const item = allItems.find(i => i.itemNo === value);
        if (item) { u.glAccount = item.glAccount; u.description = item.itemName; u.estUnitCost = item.unitPrice ?? ''; }
      }
      return u;
    }));
  };
  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (key) => setLines(prev => (prev.length === 1 ? prev : prev.filter(l => l._key !== key)));

  const lineAmt = (l) => (parseFloat(l.qty) || 0) * (parseFloat(l.estUnitCost) || 0);
  const estTotal = lines.reduce((s, l) => s + lineAmt(l), 0);

  const openCreate = () => { setEditing(null); setHeader(emptyHeader()); setLines([emptyLine()]); setError(''); setFormOpen(true); };
  const openEdit = (pr) => {
    setEditing(pr.requisitionNo);
    setHeader({
      requisitionDate: pr.requisitionDate?.split('T')[0] || '',
      requestedBy: pr.requestedBy || '', department: pr.department || '',
      locationCode: pr.locationCode || '', neededByDate: pr.neededByDate?.split('T')[0] || '',
      purpose: pr.purpose || '',
    });
    setLines((pr.lines || []).map(l => ({
      _key: Math.random(), lineType: l.lineType, itemNo: l.itemNo || '', glAccount: l.glAccount || '',
      description: l.description || '', qty: l.qty, estUnitCost: l.estUnitCost ?? '', vatGroupCode: l.vatGroupCode || '',
    })));
    setError(''); setFormOpen(true);
  };

  const handleSave = async () => {
    for (const l of lines) {
      if (l.lineType === 'GL Account' && !l.glAccount) { setError('Each GL Account line needs a GL account'); return; }
      if (l.lineType !== 'GL Account' && !l.itemNo)     { setError(`Select an item on each ${l.lineType} line`); return; }
      if (!l.description.trim()) { setError('Each line needs a description'); return; }
      if (!l.qty || parseFloat(l.qty) <= 0) { setError('Qty must be greater than zero'); return; }
    }
    setSaving(true); setError('');
    try {
      const payload = {
        ...header,
        lines: lines.map((l, i) => ({
          lineNo: i + 1, lineType: l.lineType, itemNo: l.itemNo || '', glAccount: l.glAccount || '',
          description: l.description, qty: parseFloat(l.qty), estUnitCost: parseFloat(l.estUnitCost) || 0,
          vatGroupCode: l.vatGroupCode || '',
        })),
      };
      if (editing) await axios.put(`${API}/api/payables/purchase-requisitions/${editing}`, payload, { headers });
      else         await axios.post(`${API}/api/payables/purchase-requisitions`, payload, { headers });
      setFormOpen(false);
      setSuccess(editing ? 'Requisition updated' : 'Requisition created');
      fetchReqs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save requisition');
    } finally { setSaving(false); }
  };

  const doAction = async (reqNo, action, body) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/payables/purchase-requisitions/${reqNo}/${action}`, body || {}, { headers });
      setSuccess(data.message || 'Done');
      fetchReqs();
      return data;
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action}`);
    } finally { setBusy(false); }
  };

  const handleCancel = async () => {
    try {
      await axios.delete(`${API}/api/payables/purchase-requisitions/${cancelPR}`, { headers });
      setCancelPR(null); setSuccess('Requisition cancelled'); fetchReqs();
    } catch (err) { setCancelPR(null); setError(err.response?.data?.error || 'Failed to cancel'); }
  };

  const handleReject = async () => {
    const no = rejectPR.requisitionNo;
    setRejectPR(null);
    await doAction(no, 'reject', { reason: rejReason });
    setRejReason('');
  };

  const toggleSupplier = (code) =>
    setGenSel(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);

  const handleGenerate = async () => {
    const res = await doAction(genPR.requisitionNo, 'generate-quotes', { supplierCodes: genSel });
    setGenPR(null); setGenSel([]);
    if (res?.created?.length) navigate('/payables/quotes');
  };

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading requisitions…</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Assignment sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="h5" color="primary">Purchase Requisitions</Typography>
                <Typography variant="body2" color="text.secondary">Request to buy → approve → generate RFQ quotes</Typography>
              </Box>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/payables')} title="Payables Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}><Home /></IconButton>
              <IconButton onClick={openCreate} title="New Requisition"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Add /></IconButton>
            </Box>
          </Box>

          <Toast error={error} success={success} onClose={(k) => (k === 'error' ? setError('') : setSuccess(''))} />

          {reqs.length === 0 ? (
            <Typography>No purchase requisitions yet. Create one to start the procurement flow.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Req No</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Requested By</strong></TableCell>
                  <TableCell><strong>Dept</strong></TableCell>
                  <TableCell align="center"><strong>Lines</strong></TableCell>
                  <TableCell align="right"><strong>Est. Total</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reqs.map(pr => (
                  <TableRow key={pr.requisitionNo} hover sx={{ cursor: 'pointer' }} onClick={() => setViewPR(pr)}>
                    <TableCell><strong>{pr.requisitionNo}</strong></TableCell>
                    <TableCell>{d(pr.requisitionDate)}</TableCell>
                    <TableCell>{pr.requestedBy || '—'}</TableCell>
                    <TableCell>{pr.department || '—'}</TableCell>
                    <TableCell align="center">
                      <Chip label={pr.lines?.length || 0} size="small" color="info" variant="outlined"
                        sx={{ fontWeight: 700, minWidth: 40 }} />
                    </TableCell>
                    <TableCell align="right">{fmt(pr.estTotal || 0)}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                        <Chip label={pr.status} color={STATUS_COLOR[pr.status] || 'default'} size="small" variant="outlined" />
                        {pr.quoteNos?.length > 0 && (
                          <Chip label={`${pr.quoteNos.length} quote${pr.quoteNos.length === 1 ? '' : 's'}`}
                            size="small" color="success" sx={{ fontSize: '0.65rem', height: 18 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Box display="flex" alignItems="center">
                        {['Draft', 'Rejected'].includes(pr.status) && (
                          <IconButton color="primary" size="small" onClick={() => openEdit(pr)} title="Edit"><Edit fontSize="small" /></IconButton>
                        )}
                        {pr.status === 'Draft' && (
                          <IconButton color="warning" size="small" disabled={busy}
                            onClick={() => doAction(pr.requisitionNo, 'submit')} title="Submit for approval"><Send fontSize="small" /></IconButton>
                        )}
                        {pr.status === 'Submitted' && (
                          <IconButton color="primary" size="small" disabled={busy}
                            onClick={() => doAction(pr.requisitionNo, 'approve')} title="Approve"><CheckCircle fontSize="small" /></IconButton>
                        )}
                        {pr.status === 'Submitted' && (
                          <IconButton color="error" size="small"
                            onClick={() => { setRejectPR(pr); setRejReason(''); }} title="Reject"><Cancel fontSize="small" /></IconButton>
                        )}
                        {pr.status === 'Approved' && (
                          <IconButton color="success" size="small"
                            onClick={() => { setGenPR(pr); setGenSel([]); }} title="Generate Purchase Quotes (RFQ)"><RequestQuote fontSize="small" /></IconButton>
                        )}
                        {pr.status === 'Quoted' && (
                          <IconButton color="success" size="small"
                            onClick={() => navigate('/payables/quotes')} title="View generated quotes"><ArrowForward fontSize="small" /></IconButton>
                        )}
                        {!['Quoted', 'Cancelled'].includes(pr.status) && (
                          <IconButton color="error" size="small"
                            onClick={() => setCancelPR(pr.requisitionNo)} title="Cancel"><Delete fontSize="small" /></IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Create / Edit dialog ── */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle component="div">
          <Box display="flex" alignItems="center" gap={1}>
            <Assignment color="primary" />
            <Typography variant="h6">{editing ? `Edit Requisition — ${editing}` : 'New Purchase Requisition'}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
            <TextField label="Requisition Date" type="date" size="small" InputLabelProps={{ shrink: true }}
              value={header.requisitionDate} onChange={e => setHeader(h => ({ ...h, requisitionDate: e.target.value }))} />
            <TextField label="Needed By" type="date" size="small" InputLabelProps={{ shrink: true }}
              value={header.neededByDate} onChange={e => setHeader(h => ({ ...h, neededByDate: e.target.value }))} />
            <TextField label="Requested By" size="small" sx={{ minWidth: 180 }}
              value={header.requestedBy} onChange={e => setHeader(h => ({ ...h, requestedBy: e.target.value }))}
              helperText="Defaults to you if left blank" />
            <TextField label="Department" size="small" sx={{ minWidth: 160 }}
              value={header.department} onChange={e => setHeader(h => ({ ...h, department: e.target.value }))} />
            <TextField select label="Needed at Location" size="small" sx={{ minWidth: 220 }}
              value={header.locationCode} onChange={e => setHeader(h => ({ ...h, locationCode: e.target.value }))}>
              <MenuItem value=""><em>Optional</em></MenuItem>
              {locations.map(loc => <MenuItem key={loc.locationCode} value={loc.locationCode}>{loc.locationCode} – {loc.locationName}</MenuItem>)}
            </TextField>
            <TextField label="Purpose / Justification" size="small" fullWidth multiline
              value={header.purpose} onChange={e => setHeader(h => ({ ...h, purpose: e.target.value }))} />
          </Box>

          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="subtitle2" fontWeight={600} mb={1}>Requisition Lines</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 120 }}><strong>Type</strong></TableCell>
                  <TableCell sx={{ minWidth: 200 }}><strong>Item / GL</strong></TableCell>
                  <TableCell sx={{ minWidth: 170 }}><strong>Description</strong></TableCell>
                  <TableCell align="center" sx={{ width: 80 }}><strong>Qty</strong></TableCell>
                  <TableCell align="right" sx={{ minWidth: 110 }}><strong>Est. Unit Cost</strong></TableCell>
                  <TableCell sx={{ minWidth: 120 }}><strong>VAT</strong></TableCell>
                  <TableCell align="right" sx={{ minWidth: 100 }}><strong>Est. Amount</strong></TableCell>
                  <TableCell sx={{ width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map(l => (
                  <TableRow key={l._key}>
                    <TableCell>
                      <TextField select value={l.lineType} size="small" fullWidth
                        onChange={e => updateLine(l._key, 'lineType', e.target.value)}>
                        {LINE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {l.lineType === 'GL Account' ? (
                        <TextField select value={l.glAccount} size="small" fullWidth
                          onChange={e => updateLine(l._key, 'glAccount', e.target.value)}>
                          {glAccountOptions(glAccounts)}
                        </TextField>
                      ) : (
                        <TextField select value={l.itemNo} size="small" fullWidth
                          onChange={e => updateLine(l._key, 'itemNo', e.target.value)}>
                          <MenuItem value=""><em>Select {l.lineType}</em></MenuItem>
                          {itemsOfType(l.lineType).map(i => (
                            <MenuItem key={i.itemNo} value={i.itemNo}>{i.itemNo} – {i.itemName}</MenuItem>
                          ))}
                        </TextField>
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField value={l.description} size="small" fullWidth
                        onChange={e => updateLine(l._key, 'description', e.target.value)} />
                    </TableCell>
                    <TableCell align="center">
                      <TextField value={l.qty} size="small" type="number"
                        inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
                        onChange={e => updateLine(l._key, 'qty', e.target.value)} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField value={l.estUnitCost} size="small" type="number" placeholder="0.00"
                        inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                        onChange={e => updateLine(l._key, 'estUnitCost', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField select value={l.vatGroupCode || ''} size="small" fullWidth
                        onChange={e => updateLine(l._key, 'vatGroupCode', e.target.value)}>
                        <MenuItem value=""><em>No VAT</em></MenuItem>
                        {vatGroups.map(g => <MenuItem key={g.code} value={g.code}>{g.code} ({g.vatPercent}%)</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{sym()} {fmt(lineAmt(l))}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => removeLine(l._key)} disabled={lines.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Button startIcon={<Add />} onClick={addLine} variant="outlined" size="small">Add Line</Button>
            <Typography variant="body1" fontWeight={700}>Est. Total: {sym()} {fmt(estTotal)}</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Estimated costs are indicative only — actual prices are captured on the quotes you generate after approval.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update Requisition' : 'Create Requisition'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── View dialog ── */}
      <Dialog open={Boolean(viewPR)} onClose={() => setViewPR(null)} maxWidth="md" fullWidth>
        {viewPR && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <Assignment color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewPR.requisitionNo}</Typography>
                  <Typography variant="caption" color="text.secondary">Purchase Requisition</Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewPR.status} color={STATUS_COLOR[viewPR.status] || 'default'} size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <InfoRow label="Date"          value={d(viewPR.requisitionDate)} />
              <InfoRow label="Needed By"     value={d(viewPR.neededByDate)} />
              <InfoRow label="Requested By"  value={viewPR.requestedBy} />
              <InfoRow label="Department"    value={viewPR.department} />
              <InfoRow label="Location"      value={viewPR.locationCode} />
              <InfoRow label="Purpose"       value={viewPR.purpose} />
              {viewPR.submittedBy && <InfoRow label="Submitted By" value={`${viewPR.submittedBy} · ${d(viewPR.submittedAt)}`} />}
              {viewPR.approvedBy  && <InfoRow label="Approved By"  value={`${viewPR.approvedBy} · ${d(viewPR.approvedAt)}`} />}
              {viewPR.status === 'Rejected' && <InfoRow label="Rejection Reason" value={viewPR.rejectionReason} />}
              {viewPR.quoteNos?.length > 0 && <InfoRow label="Quotes Generated" value={viewPR.quoteNos.join(', ')} />}

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="primary" gutterBottom>Lines ({viewPR.lines?.length || 0})</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Item / GL</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Est. Unit</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Est. Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(viewPR.lines || []).map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>{l.lineNo || i + 1}</TableCell>
                      <TableCell>{l.lineType}</TableCell>
                      <TableCell>{l.itemNo || l.glAccount || '—'}</TableCell>
                      <TableCell>{l.description}</TableCell>
                      <TableCell align="right">{l.qty}</TableCell>
                      <TableCell align="right">{fmt(l.estUnitCost || 0)}</TableCell>
                      <TableCell align="right">{fmt(l.estAmount || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box textAlign="right" mt={1}>
                <Typography variant="body1" fontWeight={700}>Est. Total: {sym()} {fmt(viewPR.estTotal || 0)}</Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              {['Draft', 'Rejected'].includes(viewPR.status) && (
                <Button startIcon={<Edit />} onClick={() => { const p = viewPR; setViewPR(null); openEdit(p); }}>Edit</Button>
              )}
              {viewPR.status === 'Approved' && (
                <Button color="success" variant="outlined" startIcon={<RequestQuote />}
                  onClick={() => { const p = viewPR; setViewPR(null); setGenPR(p); setGenSel([]); }}>Generate Quotes</Button>
              )}
              <Button variant="outlined" onClick={() => setViewPR(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Generate Quotes (RFQ) dialog ── */}
      <Dialog open={Boolean(genPR)} onClose={() => setGenPR(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Quotes — {genPR?.requisitionNo}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 1.5 }}>
            Pick the suppliers to send this RFQ to. Each gets a <strong>Draft</strong> Purchase Quote with the
            requisition's {genPR?.lines?.length} line{genPR?.lines?.length === 1 ? '' : 's'} — fill in each
            vendor's prices, then accept the best and convert it to a Purchase Order.
          </DialogContentText>
          {suppliers.length === 0 ? (
            <Alert severity="warning">No active suppliers found. Add a supplier first.</Alert>
          ) : (
            <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
              {suppliers.map(s => (
                <FormControlLabel key={s.supplierCode}
                  control={<Checkbox size="small" checked={genSel.includes(s.supplierCode)} onChange={() => toggleSupplier(s.supplierCode)} />}
                  label={`${s.supplierCode} – ${s.supplierName}`} sx={{ display: 'flex' }} />
              ))}
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {genSel.length} supplier{genSel.length === 1 ? '' : 's'} selected · {genSel.length} quote{genSel.length === 1 ? '' : 's'} will be created
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenPR(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleGenerate} disabled={busy || genSel.length === 0}>
            {busy ? 'Generating…' : `Generate ${genSel.length || ''} Quote${genSel.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reject dialog ── */}
      <Dialog open={Boolean(rejectPR)} onClose={() => setRejectPR(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject {rejectPR?.requisitionNo}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>The requisition goes back to Draft so it can be revised and re-submitted.</DialogContentText>
          <TextField label="Reason" fullWidth multiline minRows={2} value={rejReason} onChange={e => setRejReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectPR(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={busy}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* ── Cancel confirm ── */}
      <Dialog open={Boolean(cancelPR)} onClose={() => setCancelPR(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel {cancelPR}</DialogTitle>
        <DialogContent><DialogContentText>Cancel requisition {cancelPR}? This cannot be undone.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelPR(null)}>No</Button>
          <Button variant="contained" color="error" onClick={handleCancel}>Yes, Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseRequisitionList;
